/**
 * Vector belt visuals: colors and segment layout per belt name (all systems share color names where possible).
 */

const NAMED_COLORS = {
  white: "#F5F5F5",
  blue: "#2969AE",
  purple: "#6C3C90",
  brown: "#4F3319",
  black: "#1A1A1A",
  coral: "#E31E24",
  grey: "#7E8284",
  yellow: "#FAC840",
  orange: "#F15E3A",
  green: "#105D36",
  red: "#C62828",
  silver: "#C0C0C0",
  gold: "#D4AF37",
  /** Capoeira raw cord / unbleached */
  crua: "#C4A574",
};

/** @param {string} belt */
export function normalizeBeltToken(belt) {
  if (!belt || typeof belt !== "string") return "";
  return belt.toLowerCase().replace(/[-\s]/g, "").trim();
}

/**
 * Two-tone kids-style belts: ColorWhite, ColorBlack, GreyWhite, etc.
 * @returns {{ color: string, flex: number }[]|null}
 */
function segmentsForTwoToneToken(token) {
  const twoTone = [
    { suffix: "white", middle: "white", ends: NAMED_COLORS.white },
    { suffix: "black", middle: "black", ends: NAMED_COLORS.black },
  ];
  for (const [name, hex] of Object.entries(NAMED_COLORS)) {
    if (name === "white" || name === "black") continue;
    for (const { suffix, ends } of twoTone) {
      const key = `${name}${suffix}`;
      if (token === key) {
        return [
          { color: ends, flex: 1 },
          { color: hex, flex: 2 },
          { color: ends, flex: 1 },
        ];
      }
    }
  }
  return null;
}

/**
 * @param {string} belt
 * @param {string} [beltSystemKey]
 * @returns {{ segments: { color: string, flex: number }[], border?: string } | null}
 */
export function resolveBeltVisual(belt, beltSystemKey = "bjj") {
  const token = normalizeBeltToken(belt);
  if (!token) return null;

  const twoTone = segmentsForTwoToneToken(token);
  if (twoTone) {
    return { segments: twoTone, border: "1px solid rgba(0,0,0,0.35)" };
  }

  const solid = NAMED_COLORS[token];
  if (solid) {
    const border =
      token === "white" ? "2px solid #000000" : "1px solid rgba(0,0,0,0.25)";
    return { segments: [{ color: solid, flex: 1 }], border };
  }

  // Unknown label — neutral bar (still visible in judo/other pilots)
  if (beltSystemKey && beltSystemKey !== "bjj") {
    return {
      segments: [{ color: "#9CA3AF", flex: 1 }],
      border: "1px solid rgba(0,0,0,0.3)",
    };
  }
  return null;
}

/** Android-style BeltView for BJJ; segment vectors for other sports. */
export function shouldUseBeltView(beltSystemKey) {
  return (beltSystemKey || "bjj").toLowerCase() === "bjj";
}

/** Vector segment bar for non-BJJ sports (and when forced). */
export function shouldUseVectorBeltVisual(beltSystemKey) {
  return !shouldUseBeltView(beltSystemKey);
}

export function shouldUseVectorStripeVisual(beltSystemKey) {
  return shouldUseVectorBeltVisual(beltSystemKey);
}
